import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertBanner } from '@spark';

/**
 * Warning banner for the Data Collection AI surfaces (Generate Data with AI /
 * Dataset Retrieval Agent) when no usable model provider is configured.
 *
 * ``typeSafeOnly``: the configured provider(s) are classification-only
 * decision models (TypeSafe AI) that cannot generate text — the banner then
 * names the unsupported feature instead of the generic "no providers" text.
 *
 * "Account Settings" is a link inside the sentence (not a separate banner
 * action) that navigates to the Model Providers tab, stashing the current
 * location so the Settings back button returns here.
 */
const ProviderWarningBanner = ({ featureName, typeSafeOnly = false }) => {
    const navigate = useNavigate();
    const location = useLocation();

    const goToModelProvidersSettings = useCallback(() => {
        navigate('/settings?tab=providers', {
            state: { from: `${location.pathname}${location.search}${location.hash}` },
        });
    }, [navigate, location.pathname, location.search, location.hash]);

    const settingsLink = (
        <a
            onClick={(e) => {
                e.preventDefault();
                goToModelProvidersSettings();
            }}
            style={{
                color: 'var(--semantic-button-link-content-on-surface-default)',
                cursor: 'pointer',
            }}
        >
            Account Settings
        </a>
    );

    return (
        <AlertBanner
            className="gdai-banner"
            feedback="warning"
            showButton={false}
            showCloseButton={false}
            text={
                <span className="fs-unmask">
                    {typeSafeOnly ? (
                        <>
                            TypeSafe is not supported for {featureName}. To enable this feature,
                            add an API key or cloud credentials for another model provider in{' '}
                            {settingsLink}.
                        </>
                    ) : (
                        <>
                            To enable this feature, add an API key or cloud credentials in{' '}
                            {settingsLink}.
                        </>
                    )}
                </span>
            }
        />
    );
};

ProviderWarningBanner.propTypes = {
    featureName: PropTypes.string.isRequired,
    typeSafeOnly: PropTypes.bool,
};

export default ProviderWarningBanner;
